/**
 * Voice AI — IVR Page (Orchestrator)
 * Thin shell that owns top-level state and delegates rendering
 * to sub-components: ivr-grid, ivr-detail.
 * Pattern: agent-page.js
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { ivrService } from '../../services/ivr-service.js';
import { AbortableLoader } from '../../services/abortable-loader.js';
import { agentService } from '../../services/agent-service.js';

import './ivr-grid.js';
import './ivr-detail.js';
import '../voice-modal.js';

export class IvrPage extends LitElement {
  static properties = {
    menus: { type: Array },
    selectedMenu: { type: Object },
    agents: { type: Array },
    _loading: { type: Boolean, state: true },
    _searchQuery: { type: String, state: true },
    _statusFilter: { type: String, state: true },
    _toastVisible: { type: Boolean, state: true },
    _showCreateModal: { type: Boolean, state: true },
    _createName: { type: String, state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    ivrStyles,
    css`
      :host { display: block; animation: fadeIn var(--duration-slow) var(--ease-out) backwards; }
      .toast {
        position: fixed;
        bottom: var(--space-6);
        right: var(--space-6);
        background: var(--color-success);
        color: var(--color-text-inverse);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        box-shadow: var(--shadow-xl);
        z-index: var(--z-toast);
        transform: translateY(100px);
        opacity: 0;
        transition: all var(--duration-slow) var(--ease-out);
      }
      .toast.visible { transform: translateY(0); opacity: 1; }
    `,
  ];

  constructor() {
    super();
    this.menus = [];
    this.selectedMenu = null;
    this.agents = [];
    this._loading = true;
    this._searchQuery = '';
    this._statusFilter = '';
    this._toastVisible = false;
    this._toastMessage = '';
    this._showCreateModal = false;
    this._createName = '';
    this._loader = new AbortableLoader();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadMenus();
    this._loadAgents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._loader.abort();
  }

  /* ─── Data Loading ─── */

  async _loadMenus() {
    this._loading = true;

    const result = await this._loader.run(signal =>
      ivrService.listMenus({
        search: this._searchQuery,
        status: this._statusFilter,
      }, { signal })
    );

    if (result.aborted) return;

    if (result.error) {
      console.warn('[IVR] API unavailable:', result.error.message);
      this.menus = [];
    } else if (result.data.success && result.data.menus?.length > 0) {
      this.menus = result.data.menus.map(m => ivrService.transformMenu(m));
    } else {
      this.menus = [];
    }

    this._loading = false;
  }

  async _loadAgents() {
    try {
      const data = await agentService.listAgents({ status: 'active' });
      if (data.success) {
        this.agents = (data.agents || []).map(a => agentService.transformAgent(a));
      }
    } catch (err) {
      console.error('[IVR] Failed to load agents:', err);
    }
  }

  /* ─── Actions ─── */

  _createMenu() {
    this._createName = '';
    this._showCreateModal = true;
  }

  async _confirmCreateMenu() {
    const name = this._createName?.trim();
    if (!name) return;
    this._showCreateModal = false;
    try {
      const data = await ivrService.createMenu({ name });
      if (data.success) {
        this._loadMenus();
        this._showToast(`IVR menu "${name}" created`);
      } else {
        this._showToast('Create failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      this._showToast('Create failed: ' + err.message);
    }
  }

  /* ─── Event Handlers (from sub-components) ─── */

  _onSelectMenu(e) {
    this.selectedMenu = e.detail.menu;
  }

  _onBack() {
    this.selectedMenu = null;
  }

  _onMenuUpdated(e) {
    const updated = e.detail.menu;
    this.menus = this.menus.map(m => m.id === updated.id ? updated : m);
    this.selectedMenu = updated;
  }

  _onMenuDeleted() {
    this.selectedMenu = null;
    this._loadMenus();
  }

  _onSearch(e) {
    this._searchQuery = e.detail.query;
    this._loadMenus();
  }

  _onFilter(e) {
    this._statusFilter = e.detail.status;
    this._loadMenus();
  }

  _onToast(e) {
    this._showToast(e.detail.message);
  }

  _showToast(message) {
    this._toastMessage = message;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 3000);
  }

  /* ─── Render ─── */

  render() {
    return html`
      ${iconStyles}
      ${this.selectedMenu ? html`
        <ivr-detail
          .menu=${this.selectedMenu}
          .agents=${this.agents}
          @back=${this._onBack}
          @menu-updated=${this._onMenuUpdated}
          @menu-deleted=${this._onMenuDeleted}
          @toast=${this._onToast}
        ></ivr-detail>
      ` : html`
        <ivr-grid
          .menus=${this.menus}
          .loading=${this._loading}
          .searchQuery=${this._searchQuery}
          .statusFilter=${this._statusFilter}
          @select-menu=${this._onSelectMenu}
          @create-menu=${() => this._createMenu()}
          @search=${this._onSearch}
          @filter=${this._onFilter}
        ></ivr-grid>
      `}
      <div class="toast ${this._toastVisible ? 'visible' : ''}">
        <i class="fa-sharp fa-light fa-check"></i>
        ${this._toastMessage || 'Saved successfully'}
      </div>

      <voice-modal ?open=${this._showCreateModal} title="Create IVR Menu" width="440px"
        @modal-close=${() => { this._showCreateModal = false; }}>
        <div style="padding:var(--space-6, 24px);">
        <div class="form-group">
          <label class="form-label">Menu Name</label>
          <input
            class="form-input"
            type="text"
            placeholder="e.g. Main Menu"
            .value=${this._createName}
            @input=${(e) => { this._createName = e.target.value; }}
            @keydown=${(e) => { if (e.key === 'Enter') this._confirmCreateMenu(); }}
          />
        </div>
        </div>
        <div slot="footer">
          <button class="btn btn-secondary" @click=${() => { this._showCreateModal = false; }}>Cancel</button>
          <button class="btn btn-primary" @click=${this._confirmCreateMenu} ?disabled=${!this._createName?.trim()}>Create</button>
        </div>
      </voice-modal>
    `;
  }
}

customElements.define('ivr-page', IvrPage);
